import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Heart } from 'lucide-react';
import { useSiteInfo } from '../context/SiteSettingsContext';

const UMC_LOGO = '/images/home/umc-logo.svg';

const Footer = () => {
  const churchInfo = useSiteInfo();
  return (
    <footer className="bg-slate-900 text-slate-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Church Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="bg-white p-1.5 rounded-md flex-shrink-0">
                <img
                  src={UMC_LOGO}
                  alt="United Methodist Church logo"
                  className="h-9 w-9 object-contain"
                  width="36"
                  height="36"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div>
                <h3 className="text-white font-semibold">{churchInfo.name}</h3>
                <p className="text-xs text-slate-400">A Family of Faith in North Bangalore</p>
              </div>
            </div>
            <p className="text-sm text-slate-400">
              A welcoming community where faith feels like family.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-sm hover:text-blue-400 transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/ministries" className="text-sm hover:text-blue-400 transition-colors">
                  Ministries
                </Link>
              </li>
              <li>
                <Link to="/events" className="text-sm hover:text-blue-400 transition-colors">
                  Events
                </Link>
              </li>
              <li>
                <Link to="/resources" className="text-sm hover:text-blue-400 transition-colors">
                  Resources
                </Link>
              </li>
            </ul>
          </div>

          {/* Service Times */}
          <div>
            <h4 className="text-white font-semibold mb-4">Service Times</h4>
            <ul className="space-y-2">
              {churchInfo.serviceTimes.map((service) => (
                <li key={`${service.day}-${service.time}`} className="text-sm">
                  <span className="text-blue-400">{service.day}:</span> {service.time}
                  <br />
                  <span className="text-slate-500 text-xs">{service.type}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <span>{churchInfo.address}</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-blue-400 flex-shrink-0" />
                <a href={`mailto:${churchInfo.email}`} className="hover:text-blue-400 transition-colors">
                  {churchInfo.email}
                </a>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-blue-400 flex-shrink-0" />
                <span>{churchInfo.phone}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-400">
              © {new Date().getFullYear()} Christ Methodist Church. All rights reserved.
            </p>
            <p className="text-sm text-slate-400 flex items-center gap-2">
              <span className="flex items-center gap-1">
                Built with <Heart className="h-4 w-4 text-red-500 fill-current" /> for God's glory
              </span>
              <span className="hidden md:inline text-slate-700">·</span>
              <Link
                to="/admin/login"
                className="text-slate-500 hover:text-slate-300 text-xs"
                data-testid="footer-admin-link"
              >
                Admin
              </Link>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
